import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Select from '../Select';
import Typography from '../Typography';
import Button from '../Button';
import HotkeysPreferences from '../HotkeysPreferences';
import { ButtonEnums } from '../Button';

const APP_VERSION = process.env.VERSION_NUMBER || '1.0.1';
const APP_BUILD_NUMBER = process.env.BUILD_NUM || '1.0.1.20260810';

const UserPreferences = ({
  availableLanguages,
  defaultLanguage,
  currentLanguage,
  disabled = false,
  hotkeyDefinitions,
  hotkeyDefaults,
  onCancel = () => { },
  onSubmit = () => { },
  onReset = () => { },
  hotkeysModule,
}) => {
  const { t } = useTranslation('UserPreferencesModal');
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [state, setState] = useState({
    isDisabled: disabled,
    hotkeyErrors: {},
    hotkeyDefinitions,
    language: currentLanguage,
  });

  const onSubmitHandler = () => {
    onSubmit(state);
  };

  const onResetHandler = () => {
    setState(state => ({
      ...state,
      language: defaultLanguage,
      hotkeyDefinitions: hotkeyDefaults,
      hotkeyErrors: {},
      isDisabled: disabled,
    }));
    onReset();
  };

  const onCancelHandler = () => {
    setState({ hotkeyDefinitions });
    onCancel();
  };

  const onLanguageChangeHandler = value => {
    setState(state => ({ ...state, language: value }));
  };

  const onHotkeysChangeHandler = (id, definition, errors) => {
    setState(state => ({
      ...state,
      isDisabled: Object.values(errors).every(e => e !== undefined),
      hotkeyErrors: errors,
      hotkeyDefinitions: {
        ...state.hotkeyDefinitions,
        [id]: definition,
      },
    }));
  };

  const Section = ({ title, children }) => (
    <>
      <div className="mb-2 border-b-2 border-black">
        <Typography
          variant="inherit"
          color="primaryLight"
          className="flex pb-2 text-[16px] font-semibold !leading-[1.2]"
        >
          {title}
        </Typography>
      </div>
      <div className="mt-4 mb-8">{children}</div>
    </>
  );

  return (
    <>
      {/*<Section title={t('General')}>*/}
      {/*  <div className="flex w-72 flex-row items-center justify-center">*/}
      {/*    <Typography*/}
      {/*      variant="subtitle"*/}
      {/*      className="mr-5 h-full text-right"*/}
      {/*    >*/}
      {/*      {t('Language')}*/}
      {/*    </Typography>*/}
      {/*    <Select*/}
      {/*      isClearable={false}*/}
      {/*      onChange={onLanguageChangeHandler}*/}
      {/*      options={availableLanguages}*/}
      {/*      value={state.language}*/}
      {/*    />*/}
      {/*  </div>*/}
      {/*</Section>*/}
      <Section title={t('Access Multiple Window')}>
        <div className='mb-2'>
          <p>For Accessing Multiple window you need to install below file</p>
          <a className="inline-block rounded bg-blue-600 px-4 py-1 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-700" href="https://medimaze.in/RIS-Guide/MultipleScreenWindowInstaller.exe" target='_blank'>
            Download Link
          </a>
        </div>
      </Section>
      {hotkeysModule && (
        <Section title={t('Hotkeys')}>
          <HotkeysPreferences
            disabled={disabled}
            hotkeyDefinitions={state.hotkeyDefinitions}
            onChange={onHotkeysChangeHandler}
            errors={state.hotkeyErrors}
            hotkeysModule={hotkeysModule}
          />
        </Section>
      )}
      <Section title="About">
        <div className="flex justify-end">
          <Button
            type={ButtonEnums.type.secondary}
            onClick={() => setShowAboutDialog(true)}
          >
            About
          </Button>
        </div>
      </Section>
      <div className="flex flex-row justify-between">
        <Button
          type={ButtonEnums.type.secondary}
          onClick={onResetHandler}
          disabled={disabled}
        >
          {t('Reset to defaults')}
        </Button>
        <div className="flex flex-row">
          <Button
            type={ButtonEnums.type.secondary}
            onClick={onCancelHandler}
          >
            {t('Cancel')}
          </Button>
          <Button
            disabled={state.isDisabled}
            className="ml-2"
            onClick={onSubmitHandler}
          >
            {t('Save')}
          </Button>
        </div>
      </div>

      {showAboutDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-xl rounded-lg border border-sky-400/50 bg-[#0f1b2d] p-6 shadow-[0_0_30px_rgba(14,165,233,0.35)]">
            <button
              type="button"
              aria-label="Close about dialog"
              onClick={() => setShowAboutDialog(false)}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-sky-400/50 bg-slate-800 text-lg font-semibold text-sky-200 transition hover:border-sky-300 hover:text-white"
            >
              ×
            </button>

            <div className="mb-6 text-center">
              <Typography variant="h3" className="text-white">
                Medimaze Viewer
              </Typography>
            </div>

            <div className="space-y-4 text-white">
              <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                <Typography variant="subtitle" className="text-sky-200">
                  Version
                </Typography>
                <Typography variant="subtitle" className="font-medium text-white">
                  {APP_VERSION}
                </Typography>
              </div>
              <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                <Typography variant="subtitle" className="text-sky-200">
                  Build Number
                </Typography>
                <Typography variant="subtitle" className="font-medium text-white">
                  {APP_BUILD_NUMBER}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const noop = () => { };

UserPreferences.propTypes = {
  disabled: PropTypes.bool,
  hotkeyDefaults: PropTypes.object.isRequired,
  hotkeyDefinitions: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  hotkeysModule: PropTypes.shape({
    initialize: PropTypes.func,
    pause: PropTypes.func,
    unpause: PropTypes.func,
    startRecording: PropTypes.func,
    record: PropTypes.func,
  }),
};

export default UserPreferences;
